/**
 * Where an agent's dropouts actually happen: step + reason.
 *
 * Reasons live on job.stats.dropoutReasons so they survive the 60-day
 * archive prune. Jobs without that tree still fall back to the archive.
 * Manual removals increment skipped, not dropped, and stay out of both.
 */

import { humanizeFailReason, parsePotentialThreshold, failReasonKey } from "./outreachFailExplain";

const NOT_IN_DROPOUT = new Set(["manually_removed"]);
const DROPOUT_STATUS = new Set(["failed", "skipped"]);

const isArchiveDropout = (entry) => {
  if (!entry || entry.isTestRun) return false;
  if (!DROPOUT_STATUS.has(entry.status)) return false;
  return !NOT_IN_DROPOUT.has(dropoutReasonKey(entry.failReason));
};

export const hasStoredDropoutReasons = (stats) => {
  const node = stats && stats.dropoutReasons;
  if (!node || typeof node !== "object") return false;
  return Object.values(node).some((step) => (
    step && typeof step === "object"
    && Object.values(step).some((row) => asCount(row && row.count) > 0)
  ));
};

const TYPE_LABELS = {
  clean: "Clean / Qualify",
  web_research: "Web Research",
  instagram_research: "Instagram Research",
  google_review_audit: "Google Review Audit",
  data_connector: "Custom Researcher",
  potential_analyzer: "Potential Analyzer",
  dm_email_finder: "DM Email Finder",
  send_email: "Send Email",
  add_tag: "Add Tag",
  add_to_flow: "Add to Flow",
  wait: "Wait",
  check_reply: "Check Reply",
  end_flow: "End Flow",
};

const HEAD_REASONS = new Set([
  "status_mismatch",
  "tag_removed",
  "safeguard_rejected",
  "max_retries",
  "manually_removed",
  "contact_deleted",
  "contact_missing",
]);

const asCount = (value) => Math.max(0, Number(value) || 0);

export const dropoutReasonKey = (reason) => {
  const raw = String(reason || "").trim();
  if (!raw) return "unknown";
  if (parsePotentialThreshold(raw)) return "potential_below_threshold";
  const head = raw.split(":")[0].trim().split(/[\s(]/)[0];
  if (HEAD_REASONS.has(head)) return head;
  return failReasonKey(raw) || "unknown";
};

export const stepDropoutTitle = (step, { isPrepare = false } = {}) => {
  if (!step || typeof step !== "object") return "Unknown step";
  const type = step.type || "";
  let label = isPrepare && type === "send_email"
    ? "Prepare Email"
    : (TYPE_LABELS[type] || type || "Step");
  if (type === "send_email" && step.templateName) label += ` · ${step.templateName}`;
  if (type === "potential_analyzer" && typeof step.minScore === "number") {
    label += ` · minimum ${step.minScore}/10`;
  }
  if (type === "clean" && step.guidelineName) label += ` · ${step.guidelineName}`;
  return label;
};

const reasonLabel = (key, row) => {
  if (key === "potential_below_threshold") {
    const gate = row.minScore != null ? `the ${row.minScore}/10 gate` : "the score gate";
    const scores = (row.scores || []).filter((score) => Number.isFinite(score));
    if (scores.length === 0) return `Potential score was below ${gate}.`;
    const lo = Math.min(...scores);
    const hi = Math.max(...scores);
    const span = lo === hi ? `${lo}/10` : `${lo}–${hi}/10`;
    return `Potential score was below ${gate} (${span}).`;
  }
  return humanizeFailReason(row.sample || key);
};

const share = (count, denom) => (denom > 0 ? Math.round((count / denom) * 100) : 0);

const deletesContact = (step) => {
  if (!step) return false;
  if ((step.onFail || "remove") !== "remove") return false;
  return step.failAction === "delete" || step.failAction === "delete_and_block";
};

/**
 * @param {object} opts
 * @param {Array} opts.steps job.steps
 * @param {object} [opts.archive] outreachJobArchive map, used until a job has stored reasons
 * @param {object} [opts.storedReasons] job.stats.dropoutReasons, kept after the archive is pruned
 * @param {number} opts.dropped stats.dropped (the pill numerator)
 * @param {number} opts.attempted dropped + prepared/sent
 * @param {boolean} opts.isPrepare
 */
export const buildDropoutBreakdown = ({
  steps = [],
  archive = {},
  storedReasons = null,
  dropped = 0,
  attempted = 0,
  isPrepare = false,
} = {}) => {
  const byStep = new Map();
  let recorded = 0;

  const ensureBucket = (stepIndex) => {
    let bucket = byStep.get(stepIndex);
    if (!bucket) {
      bucket = { stepIndex, reasons: new Map() };
      byStep.set(stepIndex, bucket);
    }
    return bucket;
  };

  const addCount = (stepIndex, key, sample, count, extra = {}) => {
    const n = asCount(count);
    if (!n || NOT_IN_DROPOUT.has(key)) return;
    recorded += n;
    const bucket = ensureBucket(stepIndex);
    let row = bucket.reasons.get(key);
    if (!row) {
      row = { key, count: 0, sample: sample || key, scores: [], minScore: extra.minScore ?? null };
      bucket.reasons.set(key, row);
    }
    row.count += n;
    if (sample) row.sample = sample;
    if (extra.minScore != null) row.minScore = extra.minScore;
    (extra.scores || []).forEach((score) => {
      if (Number.isFinite(score)) row.scores.push(score);
    });
  };

  const archiveRows = [];
  Object.values(archive || {}).forEach((entry) => {
    if (!isArchiveDropout(entry)) return;
    const threshold = parsePotentialThreshold(entry.failReason);
    archiveRows.push({
      stepIndex: asCount(entry.currentStepIndex),
      key: dropoutReasonKey(entry.failReason),
      sample: entry.failReason,
      count: 1,
      minScore: threshold ? threshold.minScore : null,
      scores: threshold ? [threshold.score] : [],
    });
  });

  const storedRows = [];
  if (storedReasons && typeof storedReasons === "object") {
    Object.entries(storedReasons).forEach(([stepKey, reasons]) => {
      if (!reasons || typeof reasons !== "object") return;
      const stepIndex = asCount(stepKey);
      Object.entries(reasons).forEach(([key, row]) => {
        if (!row || typeof row !== "object") return;
        const scores = [];
        if (Number.isFinite(Number(row.scoreMin))) scores.push(Number(row.scoreMin));
        if (Number.isFinite(Number(row.scoreMax)) && Number(row.scoreMax) !== Number(row.scoreMin)) {
          scores.push(Number(row.scoreMax));
        }
        storedRows.push({
          stepIndex,
          key,
          sample: row.sample || key,
          count: asCount(row.count),
          minScore: row.minScore != null ? Number(row.minScore) : null,
          scores,
        });
      });
    });
  }

  const storedRecorded = storedRows.reduce((n, row) => n + row.count, 0);
  const archiveRecorded = archiveRows.length;
  // The job histogram survives the 60-day archive prune. While the archive
  // still holds exits the histogram has not recorded yet, it stays the source.
  const source = storedRecorded > 0 && storedRecorded >= archiveRecorded
    ? storedRows
    : archiveRows;

  source.forEach((row) => {
    addCount(row.stepIndex, row.key, row.sample, row.count, {
      minScore: row.minScore,
      scores: row.scores,
    });
  });

  const droppedCount = asCount(dropped);
  const tried = asCount(attempted);
  const unrecordedCount = Math.max(0, droppedCount - recorded);
  const denom = Math.max(droppedCount, recorded);

  const stepRows = [...byStep.values()]
    .sort((a, b) => a.stepIndex - b.stepIndex)
    .map((bucket) => {
      const step = steps[bucket.stepIndex] || null;
      const reasons = [...bucket.reasons.values()]
        .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key))
        .map((row) => ({
          key: row.key,
          label: reasonLabel(row.key, row),
          count: row.count,
          shareOfDropouts: share(row.count, denom),
          shareOfTried: share(row.count, tried),
        }));
      const count = reasons.reduce((n, row) => n + row.count, 0);
      return {
        stepIndex: bucket.stepIndex,
        title: stepDropoutTitle(step, { isPrepare }),
        count,
        shareOfDropouts: share(count, denom),
        shareOfTried: share(count, tried),
        reasons,
      };
    });

  const deletingSteps = (steps || [])
    .map((step, stepIndex) => ({ step, stepIndex }))
    .filter(({ step }) => deletesContact(step))
    .map(({ step, stepIndex }) => ({
      stepIndex,
      title: stepDropoutTitle(step, { isPrepare }),
    }));

  const unrecorded = unrecordedCount > 0
    ? {
      count: unrecordedCount,
      shareOfDropouts: share(unrecordedCount, denom),
      shareOfTried: share(unrecordedCount, tried),
      deletingSteps,
    }
    : null;

  const candidates = [];
  stepRows.forEach((step) => {
    step.reasons.forEach((reason) => {
      candidates.push({ count: reason.count, label: reason.label, where: step.title });
    });
  });
  if (unrecorded) {
    candidates.push({
      count: unrecorded.count,
      label: "No reason was saved for these contacts.",
      where: null,
    });
  }
  candidates.sort((a, b) => b.count - a.count);
  const top = candidates[0] || null;
  const lead = top
    ? (top.where
      ? `${top.count} left at ${top.where}: ${top.label} That is ${share(top.count, denom)}% of dropouts.`
      : `${top.count} were counted as dropouts, but no reason was saved. That is ${share(top.count, denom)}% of dropouts.`)
    : "";

  return {
    recorded,
    dropped: droppedCount,
    attempted: tried,
    denom,
    lead,
    steps: stepRows,
    unrecorded,
  };
};
